// hooks/use-wishlist-actions.js
import {useState, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {useCustomToast} from '../contexts/custom-toast-context'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import { clearPendingWishlist, getWishlistId, persistPendingWishlist } from '../utils/wishlist-utils'
import {useProductLists, CUSTOM_FOLDER_TYPE} from './use-product-lists'
import {useAddToCartSource} from '../contexts/add-to-cart-source-context'
import {sendWishlistEvent} from '../utils/gtm-utils'
import {sendBrazeWishlistEvent} from '../utils/braze-utils'

export const WISHLIST_MAX_ITEMS = 250

export const useWishlistActions = () => {
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const customerId = useCustomerId()
    const {showCustomToast} = useCustomToast()
    const {data: wishlist, refetch: refetchWishlist} = useWishList()
    const {allLists = [], customFolders, refetch: refetchAllLists} = useProductLists()
    const {interactionLocation} = useAddToCartSource()
    const [wishlistLoading, setWishlistLoading] = useState([])

    // Mutations
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const {mutateAsync: createCustomerProductList} = useShopperCustomersMutation(
        'createCustomerProductList'
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )
    const {mutateAsync: updateCustomerProductList} = useShopperCustomersMutation(
        'updateCustomerProductList'
    )
    const {mutateAsync: deleteCustomerProductList} = useShopperCustomersMutation(
        'deleteCustomerProductList'
    )


    // Check if product is in wishlist
    const isInWishlist = useMemo(() => {
        return (productId) => {
            if (!wishlist?.customerProductListItems || !productId) return false
            return !!wishlist.customerProductListItems.find((item) => item.productId === productId)
        }
    }, [wishlist?.customerProductListItems])

    // Check if product is loading
    const isProductLoading = useMemo(() => {
        return (productId) => wishlistLoading.includes(productId)
    }, [wishlistLoading])

    // Add item to wishlist (always targets My Favourites)
    const addItemToWishlist = async (
        product,
        containerRef = null,
        savedInteractionLocation = null
    ) => {
        const resolvedInteractionLocation = savedInteractionLocation || interactionLocation
        const currentCount = wishlist?.customerProductListItems?.length ?? 0
        if (currentCount >= WISHLIST_MAX_ITEMS) {
            showCustomToast({
                message: formatMessage({
                    id: 'wishlist.error.max_items',
                    defaultMessage: 'Your wishlist is full. Remove some items to add new ones.'
                })
            })
            return
        }

        const listId = await getWishlistId({
            wishlist,
            customerId,
            refetchWishlist,
            createCustomerProductList
        })
        if (!listId || !customerId) {
            if (typeof window !== 'undefined' && !customerId && product?.productId) {
                persistPendingWishlist({
                    productId: product.productId,
                    interactionLocation: resolvedInteractionLocation
                })
            }
            console.warn('Wishlist or customer not available')
            return
        }

        setWishlistLoading((prev) => [...prev, product.productId])

        try {
            await createCustomerProductListItem({
                parameters: {customerId, listId},
                body: {
                    quantity: 1,
                    public: false,
                    priority: 1,
                    type: 'product',
                    productId: product.productId
                }
            })

            clearPendingWishlist()

            showCustomToast({
                message: formatMessage({
                    id: 'cart.info.item_added',
                    defaultMessage: 'Added to Wishlist'
                }),
                actionLabel: formatMessage({
                    id: 'cart.info.view_list',
                    defaultMessage: 'View List'
                }),
                onActionClick: () => navigate('/wishlist')
            })
            sendWishlistEvent(product, false, undefined, resolvedInteractionLocation)

            // Braze Modification - Send Braze Wishlist Event
            sendBrazeWishlistEvent(product, false, product?.primaryCategoryId)
        } catch (error) {
            console.error('Error adding to wishlist:', error)
            showCustomToast({
                message: formatMessage(API_ERROR_MESSAGE)
            })
        } finally {
            setWishlistLoading((prev) => prev.filter((id) => id !== product.productId))
        }
    }

    // Remove item from wishlist (My Favourites)
    const removeItemFromWishlist = async (product, containerRef = null, context) => {
        if (!wishlist?.id || !customerId) {
            console.warn('Wishlist or customer not available')
            return
        }

        const wishlistItem = wishlist.customerProductListItems?.find(
            (item) => item.productId === product.productId
        )

        if (!wishlistItem) {
            console.warn('Product not found in wishlist')
            return
        }

        setWishlistLoading((prev) => [...prev, product.productId])

        try {
            await deleteCustomerProductListItem({
                parameters: {
                    customerId,
                    listId: wishlist.id,
                    itemId: wishlistItem.id
                }
            })
            if (context === 'wishlist-cart-carousel-context') {
                await refetchAllLists()
            }

            // Also remove from every custom folder/collection that contains this product
            const folderRemovals = (customFolders ?? []).flatMap((folder) => {
                const fi = folder.customerProductListItems?.find(
                    (i) => i.productId === product.productId
                )
                return fi ? [{listId: folder.id, itemId: fi.id}] : []
            })
            for (const {listId, itemId} of folderRemovals) {
                await deleteCustomerProductListItem({
                    parameters: {customerId, listId, itemId}
                })
            }

            if (context !== 'add-to-cart-context') {
                showCustomToast({
                    message: formatMessage({
                        id: 'wishlist.info.item_removed',
                        defaultMessage: 'Item removed'
                    })
                })
            }
            sendWishlistEvent(product, true, undefined, interactionLocation)

            // Braze Modification - Send Braze Wishlist Event
            const brazeProduct = product?.currentProduct ?? product
            sendBrazeWishlistEvent(brazeProduct, true, brazeProduct?.primaryCategoryId)
        } catch (error) {
            console.error('Error removing from wishlist:', error)
            showCustomToast({
                message: formatMessage(API_ERROR_MESSAGE)
            })
        } finally {
            setWishlistLoading((prev) => prev.filter((id) => id !== product.productId))
        }
    }

    // Toggle wishlist status
    const toggleWishlist = async (product, containerRef = null) => {
        if (isInWishlist(product.productId)) {
            await removeItemFromWishlist(product, containerRef)
        } else {
            await addItemToWishlist(product, containerRef)
        }
    }

    // Toggle wishlist public/private status
    // listId defaults to the root wishlist; pass a folder id to toggle a specific list
    const toggleWishlistPublicStatus = async (isPublic, listId) => {
        const targetListId = listId ?? wishlist?.id
        if (!targetListId || !customerId) {
            console.warn('Wishlist or customer not available')
            return
        }

        try {
            await updateCustomerProductList({
                parameters: {customerId, listId: targetListId},
                body: {
                    public: isPublic
                }
            })

            // Clear clipboard when making wishlist private
            if (!isPublic) {
                try {
                    await navigator.clipboard.writeText('')
                } catch (clipboardError) {
                    console.warn('Could not clear clipboard:', clipboardError)
                }
            }
        } catch (error) {
            console.error('Error updating wishlist privacy:', error)
        }
    }

    // ----- Folder management -----

    // Remove item from a specific list only
    const removeItemFromList = async ({listId, itemId, productId}) => {
        if (!customerId || !listId || !itemId) return
        setWishlistLoading((prev) => [...prev, productId])
        try {
            await deleteCustomerProductListItem({parameters: {customerId, listId, itemId}})
            await refetchAllLists()
        } catch (error) {
            console.error('Error removing item from list:', error)
            showCustomToast({message: formatMessage(API_ERROR_MESSAGE)})
        } finally {
            setWishlistLoading((prev) => prev.filter((id) => id !== productId))
        }
    }

    // Remove multiple items from a list with undo support.
    // Follows TSD spec:
    //   - 1 item  → standard deleteCustomerProductListItem
    //   - N items → deleteCustomerProductListItem(firstItemId) + c_productsToRemove query param
    //     with the remaining productIds (comma-separated); server afterDELETE hook handles bulk removal
    const removeItemsFromList = async ({listId, items, onUndo, skipToast}) => {
        if (!customerId || !listId || !items?.length) return
        const productIds = items.map((i) => i.productId)
        setWishlistLoading((prev) => [...prev, ...productIds])
        try {
            if (items.length === 1) {
                // Scenario A — single item: standard SCAPI call
                await deleteCustomerProductListItem({
                    parameters: {customerId, listId, itemId: items[0].itemId}
                })
            } else {
                // Scenario B — bulk: first itemId in path, remaining productIds via c_productsToRemove
                await deleteCustomerProductListItem({
                    parameters: {
                        customerId,
                        listId,
                        itemId: items[0].itemId,
                        c_productsToRemove: items.slice(1).map((i) => i.productId).join(',')
                    }
                })
            }
            if (!skipToast) {
            const count = items.length
            const toastMessage =
                count === 1
                    ? formatMessage(
                          {id: 'wishlist.info.item_removed_count', defaultMessage: '{count} item has been removed.'},
                          {count}
                      )
                    : formatMessage(
                          {id: 'wishlist.info.items_removed_count', defaultMessage: '{count} items have been removed.'},
                          {count}
                      )
            showCustomToast({
                message: toastMessage,
                ...(onUndo && {
                    actionLabel: formatMessage({id: 'wishlist.action.undo', defaultMessage: 'Undo'}),
                    onActionClick: onUndo
                })
            })
            }
            await refetchAllLists()
        } catch (error) {
            console.error('Error batch-removing items:', error)
            showCustomToast({message: formatMessage(API_ERROR_MESSAGE)})
        } finally {
            setWishlistLoading((prev) => prev.filter((id) => !productIds.includes(id)))
        }
    }

    // Move item(s) to one or more target lists.
    // TSD spec:
    //   Step 1 — Remove from source (skip if source is My Favourites)
    //     → same delete logic as removeItemsFromList (1 item = standard, N = c_productsToRemove)
    //   Step 2 — Add to each destination via c_productsToAdd (max 30 per request, sequential)
    const moveItemsToList = async ({fromListId, wishListId, toListIds, items, skipToast}) => {
        const targetIds = Array.isArray(toListIds) ? toListIds : [toListIds]
        if (!customerId || !targetIds.length || !items?.length) return
        const isFromRoot = fromListId === wishListId
        const productIds = items.map((i) => i.productId)
        const BATCH_LIMIT = 30
        // productIds actually sent to each destination, so Undo only removes what was added.
        const addedByListId = {}
        setWishlistLoading((prev) => [...prev, ...productIds])
        try {
            // Step 1: remove from source (only if not My Favourites)
            if (!isFromRoot && fromListId) {
                if (items.length === 1) {
                    await deleteCustomerProductListItem({
                        parameters: {customerId, listId: fromListId, itemId: items[0].itemId}
                    })
                } else {
                    await deleteCustomerProductListItem({
                        parameters: {
                            customerId,
                            listId: fromListId,
                            itemId: items[0].itemId,
                            c_productsToRemove: items.slice(1).map((i) => i.productId).join(',')
                        }
                    })
                }
            }

            // Step 2: add to each target — batches of max 30, sequential
            for (const toListId of targetIds) {
                // Products already present in the destination are skipped: the SCAPI mutation
                // optimistically appends the item to the cached list, so sending a duplicate
                // would briefly inflate the destination count until the refetch corrects it.
                const existingProductIds = new Set(
                    (allLists.find((l) => l.id === toListId)?.customerProductListItems ?? []).map(
                        (it) => it.productId
                    )
                )
                const productIdsToAdd = productIds.filter((id) => !existingProductIds.has(id))
                if (!productIdsToAdd.length) continue
                addedByListId[toListId] = productIdsToAdd

                const batches = []
                for (let i = 0; i < productIdsToAdd.length; i += BATCH_LIMIT) {
                    batches.push(productIdsToAdd.slice(i, i + BATCH_LIMIT))
                }
                for (const batch of batches) {
                    if (batch.length === 1) {
                        // Single item: standard call (no custom param needed)
                        await createCustomerProductListItem({
                            parameters: {customerId, listId: toListId},
                            body: {quantity: 1, public: false, priority: 1, type: 'product', productId: batch[0]}
                        })
                    } else {
                        // Batch: first productId in body + c_productsToAdd for the rest
                        await createCustomerProductListItem({
                            parameters: {
                                customerId,
                                listId: toListId,
                                c_productsToAdd: batch.join(',')
                            },
                            body: {quantity: 1, public: false, priority: 1, type: 'product', productId: batch[0]}
                        })
                    }
                }
            }

            if (!skipToast) {
            showCustomToast({
                message: formatMessage({id: 'wishlist.info.items_moved', defaultMessage: 'Items moved to collection'})
            })
            }
            await refetchAllLists()
        } catch (error) {
            console.error('Error moving items to folder:', error)
            showCustomToast({message: formatMessage(API_ERROR_MESSAGE)})
        } finally {
            setWishlistLoading((prev) => prev.filter((id) => !productIds.includes(id)))
        }
        return {addedByListId}
    }

    // Reverts a previous moveItemsToList (used by the "Undo" toast action).
    // The move already happened, so we mirror it:
    //   - Remove the moved products from every destination list.
    //   - If they were moved OUT of a custom folder (not My Favourites), add them back to it.
    // Destination itemIds differ from the original source itemIds, so we resolve them from a
    // fresh fetch of the lists before deleting.
    const revertMoveItemsToList = async ({
        fromListId,
        wishListId,
        toListIds,
        items,
        addedByListId
    }) => {
        const targetIds = Array.isArray(toListIds) ? toListIds : [toListIds]
        if (!customerId || !targetIds.length || !items?.length) return
        const isFromRoot = fromListId === wishListId
        const productIds = items.map((i) => i.productId)
        const productIdSet = new Set(productIds)
        const BATCH_LIMIT = 30
        setWishlistLoading((prev) => [...prev, ...productIds])
        try {
            // Resolve the itemIds the moved products currently have inside each destination list.
            const refetchResult = await refetchAllLists()
            const freshLists = refetchResult?.data?.data ?? []

            // Step 1 — remove the moved products from every destination list
            for (const toListId of targetIds) {
                const list = freshLists.find((l) => l.id === toListId)
                // Only undo what the move actually added; products that already lived in the
                // destination must stay there.
                const removableIds = addedByListId
                    ? new Set(addedByListId[toListId] ?? [])
                    : productIdSet
                const targetItems = (list?.customerProductListItems ?? [])
                    .filter((it) => removableIds.has(it.productId))
                    .map((it) => ({itemId: it.id, productId: it.productId}))
                if (!targetItems.length) continue
                if (targetItems.length === 1) {
                    await deleteCustomerProductListItem({
                        parameters: {customerId, listId: toListId, itemId: targetItems[0].itemId}
                    })
                } else {
                    await deleteCustomerProductListItem({
                        parameters: {
                            customerId,
                            listId: toListId,
                            itemId: targetItems[0].itemId,
                            c_productsToRemove: targetItems.slice(1).map((i) => i.productId).join(',')
                        }
                    })
                }
            }

            // Step 2 — if the products were moved out of a custom folder, put them back
            if (!isFromRoot && fromListId) {
                const batches = []
                for (let i = 0; i < productIds.length; i += BATCH_LIMIT) {
                    batches.push(productIds.slice(i, i + BATCH_LIMIT))
                }
                for (const batch of batches) {
                    if (batch.length === 1) {
                        await createCustomerProductListItem({
                            parameters: {customerId, listId: fromListId},
                            body: {quantity: 1, public: false, priority: 1, type: 'product', productId: batch[0]}
                        })
                    } else {
                        await createCustomerProductListItem({
                            parameters: {customerId, listId: fromListId, c_productsToAdd: batch.join(',')},
                            body: {quantity: 1, public: false, priority: 1, type: 'product', productId: batch[0]}
                        })
                    }
                }
            }

            await refetchAllLists()
        } catch (error) {
            console.error('Error reverting move:', error)
            showCustomToast({message: formatMessage(API_ERROR_MESSAGE)})
        } finally {
            setWishlistLoading((prev) => prev.filter((id) => !productIds.includes(id)))
        }
    }

    // Create a new custom folder
    const createFolder = async (name, isPublic = false) => {
        if (!customerId) return null
        try {
            const result = await createCustomerProductList({
                parameters: {customerId},
                body: {type: CUSTOM_FOLDER_TYPE, name, public: isPublic}
            })
            await refetchAllLists()
            return result
        } catch (error) {
            console.error('Error creating folder:', error)
            showCustomToast({message: formatMessage(API_ERROR_MESSAGE)})
            return null
        }
    }

    // Rename a custom folder
    const renameFolder = async (listId, name) => {
        if (!customerId || !listId) return
        try {
            await updateCustomerProductList({parameters: {customerId, listId}, body: {name}})
            await refetchAllLists()
        } catch (error) {
            console.error('Error renaming folder:', error)
            showCustomToast({message: formatMessage(API_ERROR_MESSAGE)})
        }
    }

    // Delete a custom folder (and all its items)
    const deleteFolder = async (listId) => {
        if (!customerId || !listId) return
        try {
            await deleteCustomerProductList({parameters: {customerId, listId}})
            await refetchAllLists()
        } catch (error) {
            console.error('Error deleting folder:', error)
            showCustomToast({message: formatMessage(API_ERROR_MESSAGE)})
        }
    }

    /**
     * Delete a folder without showing a toast (caller manages feedback).
     * If keepItems=false, also removes the folder's items from My Favourites.
     *
     * Item removal follows the TSD spec:
     *   - 1 item  → standard deleteCustomerProductListItem
     *   - N items → deleteCustomerProductListItem(firstItemId) + c_productsToRemove query param
     *     containing the remaining productIds (comma-separated, server hook handles bulk removal)
     *
     * @param {string} folderId - ID of the custom folder to delete (must be type custom_1)
     * @param {boolean} keepItems - if true, only the folder is deleted; items stay in My Favourites
    * @param {string[]} folderProductIds - productIds of items in the folder (used when keepItems=false)
    * @param {boolean} skipRefetch - when true, avoids refetchAllLists (caller handles navigation/update)
     */
    const deleteFolderWithOptions = async ({folderId, keepItems, folderProductIds = [], skipRefetch = false}) => {
        if (!customerId || !folderId) return

        // Guard: never attempt to delete the main wishlist on the client side.
        // The backend hook (dw.ocapi.shop.customer.product_list.beforeDELETE) also
        // enforces this, returning MAIN_LIST_CANNOT_BE_DELETED if the type is not custom_1.
        if (wishlist?.id === folderId) {
            console.error('deleteFolderWithOptions: attempted to delete main wishlist — blocked.')
            showCustomToast({message: formatMessage(API_ERROR_MESSAGE)})
            return
        }

        try {
            // Step 1 (keepItems=false): remove the items from My Favourites as well.
            // The custom folder's own items are removed automatically when the list is deleted.
            if (!keepItems && folderProductIds.length > 0) {
                const wishlistItems = wishlist?.customerProductListItems ?? []
                const mainItemsToRemove = wishlistItems.filter((item) =>
                    folderProductIds.includes(item.productId)
                )

                if (mainItemsToRemove.length === 1) {
                    // Scenario A — single item: standard SCAPI call
                    await deleteCustomerProductListItem({
                        parameters: {customerId, listId: wishlist.id, itemId: mainItemsToRemove[0].id}
                    })
                } else if (mainItemsToRemove.length > 1) {
                    // Scenario B — bulk: first itemId in the path, rest via c_productsToRemove
                    // The afterDELETE hook on the server reads c_productsToRemove and deletes them
                    await deleteCustomerProductListItem({
                        parameters: {
                            customerId,
                            listId: wishlist.id,
                            itemId: mainItemsToRemove[0].id,
                            c_productsToRemove: mainItemsToRemove.slice(1).map((i) => i.productId).join(',')
                        }
                    })
                }
            }

            // Step 2: delete the custom folder (also removes all of its items server-side)
            await deleteCustomerProductList({parameters: {customerId, listId: folderId}})
            if (!skipRefetch) {
                await refetchAllLists()
            }
        } catch (error) {
            console.error('Error deleting folder with options:', error)
            showCustomToast({message: formatMessage(API_ERROR_MESSAGE)})
        }
    }

    return {
        isInWishlist,
        isProductLoading,
        addItemToWishlist,
        removeItemFromWishlist,
        removeItemFromList,
        removeItemsFromList,
        moveItemsToList,
        revertMoveItemsToList,
        toggleWishlist,
        toggleWishlistPublicStatus,
        createFolder,
        renameFolder,
        deleteFolder,
        deleteFolderWithOptions,
        wishlistLoading
    }
}
